import { useEffect, useState } from "react";
import { Hotel, ExternalLink, Search, Loader2, BookmarkPlus, Check } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/* Reusable hotel search — Stage 1 deep-link mode. Lists enabled booking providers
   from the Travel Commerce engine; every outbound click is tracked (TPCLICK).
   Also lets signed-in users save the searched stay to a trip as "Considering". */

export function getTcSession() {
  let s = localStorage.getItem("tp_tc_session");
  if (!s) { s = crypto.randomUUID(); localStorage.setItem("tp_tc_session", s); }
  return s;
}

const inp = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-teal-600";
const lbl = "block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1";

export default function HotelProviderSearch({ initial = {}, tripId = null, campaign = "TP-HOTELS", placement = "hotels-page", heading = "Search stays for your dates" }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    place: initial.place || "", checkin: initial.checkin || "", checkout: initial.checkout || "",
    adults: initial.adults || 2, children: 0, rooms: 1,
  });
  const [providers, setProviders] = useState(null);
  const [disclosure, setDisclosure] = useState("");
  const [opening, setOpening] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [trips, setTrips] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/travel/hotels/providers")
      .then((r) => { setProviders(r.data.providers); setDisclosure(r.data.disclosure); })
      .catch(() => setProviders([]));
  }, []);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const ready = f.place.trim().length > 0;

  const open = async (p) => {
    setOpening(p.id);
    try {
      const r = await api.post("/travel/affiliate/click", {
        provider_id: p.id, destination: f.place.trim(), checkin: f.checkin || null,
        checkout: f.checkout || null, adults: Number(f.adults) || 2,
        children: Number(f.children) || 0, rooms: Number(f.rooms) || 1,
        trip_id: tripId, campaign, page: window.location.pathname, placement,
        session_id: getTcSession(),
      });
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.go_url}`, "_blank", "noopener");
    } catch {
      toast.error("Could not open the booking site — try again");
    } finally { setOpening(null); }
  };

  const openSave = async () => {
    setSaveOpen(true);
    if (trips === null) {
      try { const r = await api.get("/trips"); setTrips(r.data.items || r.data || []); }
      catch { setTrips([]); }
    }
  };

  const saveToTrip = async (tid) => {
    try {
      let id = tid;
      if (!id) {  // create a brand-new trip first
        const r = await api.post("/trips", {
          title: `Trip to ${f.place.trim()}`,
          start_date: f.checkin || null, end_date: f.checkout || f.checkin || null,
          stops: [f.place.trim()],
        });
        id = r.data.id;
      }
      await api.post(`/travel/trips/${id}/stays`, {
        hotel_name: `Stay in ${f.place.trim()}`, destination: f.place.trim(),
        checkin: f.checkin || null, checkout: f.checkout || null, status: "considering",
      });
      setSaved(true); setSaveOpen(false);
      toast.success(tid ? "Saved as a Considering stay" : "New trip created with this stay", {
        action: { label: "Open trip", onClick: () => window.location.assign(`/trips/${id}`) },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  return (
    <div className="bg-white border border-zinc-300" data-testid="hotel-search-card">
      <div className="h-1 bg-teal-600" />
      <div className="p-5">
        <div className="font-heading font-900 text-lg mb-3 flex items-center gap-2">
          <Search className="w-5 h-5 text-teal-700" /> {heading}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2"><label className={lbl}>Where</label>
            <input value={f.place} onChange={set("place")} className={inp} placeholder="City, region or hotel" data-testid="hotel-search-place" /></div>
          <div><label className={lbl}>Check-in</label>
            <input type="date" value={f.checkin} onChange={set("checkin")} className={inp} data-testid="hotel-search-checkin" /></div>
          <div><label className={lbl}>Check-out</label>
            <input type="date" value={f.checkout} onChange={set("checkout")} className={inp} data-testid="hotel-search-checkout" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={lbl}>Guests</label>
              <input type="number" min="1" max="16" value={f.adults} onChange={set("adults")} className={inp} data-testid="hotel-search-guests" /></div>
            <div><label className={lbl}>Rooms</label>
              <input type="number" min="1" max="8" value={f.rooms} onChange={set("rooms")} className={inp} data-testid="hotel-search-rooms" /></div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4" data-testid="hotel-provider-buttons">
          {providers === null ? (
            <span className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading booking partners…</span>
          ) : providers.map((p) => (
            <button key={p.id} onClick={() => open(p)} disabled={!ready || opening === p.id}
              data-testid={`hotel-provider-${p.id}`}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-teal-600 text-teal-700 text-sm font-700 hover:bg-teal-50 disabled:opacity-40 first:bg-teal-700 first:text-white first:hover:bg-teal-800">
              {opening === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hotel className="w-4 h-4" />}
              {p.name} <ExternalLink className="w-3.5 h-3.5" />
            </button>
          ))}
          {user && (
            saved ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-2.5 text-emerald-700 text-sm font-700"><Check className="w-4 h-4" /> Saved</span>
            ) : (
              <button onClick={openSave} disabled={!ready} data-testid="hotel-save-to-trip"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-zinc-300 text-zinc-700 text-sm font-700 hover:bg-zinc-50 disabled:opacity-40">
                <BookmarkPlus className="w-4 h-4" /> Save to trip
              </button>
            )
          )}
        </div>

        {saveOpen && (
          <div className="mt-3 border border-zinc-200 bg-zinc-50 p-3" data-testid="save-stay-trip-picker">
            <div className="text-xs font-700 text-zinc-600 mb-2">Save "{f.place}" as a Considering stay in:</div>
            {trips === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <div className="flex flex-wrap gap-2">
                <button onClick={() => saveToTrip(null)} data-testid="save-stay-new-trip"
                  className="px-3 py-1.5 bg-teal-700 text-white text-xs font-800 hover:bg-teal-800">
                  + New trip
                </button>
                {trips.slice(0, 8).map((t) => (
                  <button key={t.id} onClick={() => saveToTrip(t.id)} data-testid={`save-stay-trip-${t.id}`}
                    className="px-3 py-1.5 border border-teal-600 text-teal-700 text-xs font-700 hover:bg-teal-50">
                    {t.title}
                  </button>
                ))}
                <button onClick={() => setSaveOpen(false)} className="px-3 py-1.5 text-xs text-muted-foreground">Cancel</button>
              </div>
            )}
          </div>
        )}

        {disclosure && (
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="affiliate-disclosure">{disclosure}</p>
        )}
      </div>
    </div>
  );
}
